import React from "react";
import { normalizeArabicText } from "../utils/arabicNormalizer";

const highlightTopicText = (text, search) => {
    if (!search || !search.trim() || !text) return <span>{text}</span>;
    
    const normalizedText = normalizeArabicText(text.toLowerCase());
    const normalizedSearch = normalizeArabicText(search.toLowerCase().trim());
    
    const startIndex = normalizedText.indexOf(normalizedSearch);
    if (startIndex === -1) {
        return <span>{text}</span>;
    }
    
    const origToNormMap = [];
    let normIndex = 0;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        const normalizedC = normalizeArabicText(c);
        if (normalizedC === "") {
            origToNormMap.push(normIndex);
        } else {
            origToNormMap.push(normIndex);
            normIndex += normalizedC.length;
        }
    }
    
    const endIndex = startIndex + normalizedSearch.length;
    
    let origStart = -1;
    let origEnd = -1;
    
    for (let i = 0; i < text.length; i++) {
        if (origToNormMap[i] === startIndex && origStart === -1) {
            origStart = i;
        }
        if (origToNormMap[i] === endIndex && origEnd === -1) {
            origEnd = i;
        }
    }
    
    if (origEnd === -1) {
        origEnd = text.length;
    }
    if (origStart === -1) {
        return <span>{text}</span>;
    }
    
    const before = text.substring(0, origStart);
    const match = text.substring(origStart, origEnd);
    const after = text.substring(origEnd);
    
    return (
        <span>
            {before}
            <mark className="search-highlight-match">{match}</mark>
            {after}
        </span>
    );
};

const CategoryTree = ({
    topicSearchQuery,
    setTopicSearchQuery,
    showCreateForm,
    setShowCreateForm,
    handleCreateTopic,
    newTopicName,
    setNewTopicName,
    newTopicCategoryId,
    setNewTopicCategoryId,
    categoriesList,
    loadingTree,
    filteredCategoriesTree,
    openCategoryId,
    toggleCategoryCollapse,
    selectedTopic,
    setSelectedTopic,
    editingTopicId,
    setEditingTopicId,
    editingTopicName,
    setEditingTopicName,
    handleRenameTopic,
    startEditing,
    t
}) => {
    return (
        <div className="col-lg-4">
            <div className="admin-card sidebar-tree-card glass-panel">
                <div className="card-header-premium header-flex-tree">
                    <h4 className="card-title-premium">
                        <i className="fal fa-sitemap card-icon-gradient"></i>
                        <span>{t("QuestionView.topicsTree")}</span>
                    </h4>
                    <button 
                        className={`btn-add-topic-premium ${showCreateForm ? 'active' : ''}`}
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        title={t("QuestionView.addNewTopic")}
                    >
                        <i className="fal fa-plus-circle"></i>
                    </button>
                </div>

                <div className="sidebar-search-wrapper">
                    <div className="search-premium-box">
                        <i className="fal fa-search search-icon-premium"></i>
                        <input
                            type="text"
                            className="form-control search-input-premium"
                            placeholder={t("QuestionView.searchTopics")}
                            value={topicSearchQuery}
                            onChange={(e) => setTopicSearchQuery(e.target.value)}
                        />
                        {topicSearchQuery && (
                            <button className="search-clear-btn" onClick={() => setTopicSearchQuery("")}>
                                <i className="fal fa-times-circle"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Create Topic with Category Selection */}
                {showCreateForm && (
                    <div className="create-topic-form-wrapper">
                        <form onSubmit={handleCreateTopic}>
                            <div className="form-group mb-3">
                                <label className="form-label-premium">{t("QuestionView.topicNameLabel")}</label>
                                <input
                                    type="text"
                                    className="form-control topic-input-premium"
                                    placeholder={t("QuestionView.topicPlaceholder")}
                                    value={newTopicName}
                                    onChange={(e) => setNewTopicName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="form-group mb-3">
                                <label className="form-label-premium">{t("QuestionView.category")}</label>
                                <select
                                    className="form-control topic-select-premium"
                                    value={newTopicCategoryId}
                                    onChange={(e) => setNewTopicCategoryId(e.target.value)}
                                >
                                    <option value="">-- {t("QuestionView.uncategorized")} --</option>
                                    {categoriesList.map(cat => (
                                        <option key={cat.CategoryId} value={cat.CategoryId}>
                                            {cat.CategoryName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="d-flex gap-2 justify-content-end mt-2">
                                <button type="submit" className="btn btn-save-premium">
                                    <i className="fal fa-check ml-1"></i>
                                    {t("QuestionView.save")}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-cancel-premium"
                                    onClick={() => { 
                                        setShowCreateForm(false); 
                                        setNewTopicName("");
                                        setNewTopicCategoryId("");
                                    }}
                                >
                                    {t("QuestionView.cancel")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Tree List */}
                <div className="topics-list-wrapper-premium">
                    {loadingTree ? (
                        <div className="sidebar-loader text-center my-5">
                            <div className="loader-ring"><div></div><div></div><div></div><div></div></div>
                            <p className="mt-3 text-muted small">{t("QuestionView.pleaseWait")}</p>
                        </div>
                    ) : filteredCategoriesTree.length === 0 ? (
                        <div className="text-center my-5 text-muted no-results-box">
                            <i className="fal fa-sitemap empty-icon-sidebar"></i>
                            <p>{topicSearchQuery ? t("QuestionView.noSearchResults") : t("QuestionView.noCategoriesOrTopics")}</p>
                        </div>
                    ) : (
                        <div className="category-tree-container">
                            {filteredCategoriesTree.map((category) => {
                                const isCollapsed = openCategoryId !== category.CategoryId;
                                
                                return (
                                    <div key={category.CategoryId} className="category-tree-node">
                                        {/* Category Header (collapsible) */}
                                        <div 
                                            className="category-node-header"
                                            onClick={() => toggleCategoryCollapse(category.CategoryId)}
                                        >
                                            <div className="header-info-flex">
                                                <i className={`fal ${isCollapsed ? 'fa-chevron-left' : 'fa-chevron-down'} collapse-arrow`}></i>
                                                <i className="fal fa-folder-open category-folder-icon"></i>
                                                <span className="category-node-title">
                                                    {category.CategoryName}
                                                    <span className="category-node-topics-count" style={{ fontSize: '0.8rem', color: 'var(--text-slate-500)', marginRight: '8px' }}>
                                                        ({category.Topics ? category.Topics.length : 0} {t("QuestionView.topicCountLabel")})
                                                    </span>
                                                </span>
                                            </div>
                                            <span className="category-node-count-badge" title={t("QuestionView.totalQuestions")}>
                                                {category.TotalQuestionCount} {t("QuestionView.questionCount")}
                                            </span>
                                        </div>

                                        {/* Topics List (Children) */}
                                        {!isCollapsed && (
                                            <ul className="category-node-children">
                                                {category.Topics && category.Topics.length > 0 ? (
                                                    category.Topics.map((topic) => {
                                                        const isSelected = selectedTopic && selectedTopic.TopicId === topic.TopicId;
                                                        const isEditing = editingTopicId === topic.TopicId;

                                                        return (
                                                            <li 
                                                                key={topic.TopicId} 
                                                                className={`topic-tree-item ${isSelected ? 'active' : ''}`}
                                                            >
                                                                {isEditing ? (
                                                                    <form onSubmit={(e) => handleRenameTopic(e, topic.TopicId)} className="w-100 d-flex gap-1 align-items-center">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control rename-input-premium"
                                                                            value={editingTopicName}
                                                                            onChange={(e) => setEditingTopicName(e.target.value)}
                                                                            autoFocus
                                                                        />
                                                                        <button type="submit" className="btn btn-save-action-action" title={t("QuestionView.save")}>
                                                                            <i className="fal fa-check"></i>
                                                                        </button>
                                                                        <button 
                                                                            type="button" 
                                                                            className="btn btn-cancel-action-action" 
                                                                            onClick={() => setEditingTopicId(null)}
                                                                            title={t("QuestionView.cancel")}
                                                                        >
                                                                            <i className="fal fa-times"></i>
                                                                        </button>
                                                                    </form>
                                                                ) : (
                                                                    <div className="topic-item-flex-layout">
                                                                        <div 
                                                                            className="topic-clickable-target"
                                                                            onClick={() => setSelectedTopic(topic)}
                                                                        >
                                                                            <i className="fal fa-file-alt topic-icon-bullet"></i>
                                                                            <span className="topic-title-span">{highlightTopicText(topic.TopicName, topicSearchQuery)}</span>
                                                                            <span className="topic-question-count">({topic.QuestionCount})</span>
                                                                        </div>
                                                                        <div className="topic-item-actions">
                                                                            <button 
                                                                                className="btn-rename-topic-premium"
                                                                                onClick={() => startEditing(topic)}
                                                                                title={t("QuestionView.renameTopic")}
                                                                            >
                                                                                <i className="fal fa-edit"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </li>
                                                        );
                                                    })
                                                ) : (
                                                    <li className="topic-tree-item-empty">
                                                        {t("QuestionView.noTopicsInCategory")}
                                                    </li>
                                                )}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CategoryTree;
